import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTonConnectUI, useTonWallet } from "@tonconnect/ui-react";
import { colors } from "../theme";
import Header from "../components/Header";
import SwipeTrack from "../components/SwipeTrack";
import ProcessingOverlay, { type ProcessingPhase } from "../components/ProcessingOverlay";
import api from "../api";
import { cleanDescription } from "../utils/cleanDescription";
import { useUser } from "../hooks/useAuth";

const FUND_VERIFY_ATTEMPTS = 6;

interface FundState {
  activity?: string;
  target?: string;
  deadline?: string;
  amount: string;
  targetUser: { telegram_id: number; name: string; username?: string } | null;
  targetUsername?: string;
  description: string;
  category?: string;
  expires_in_hours?: number;
  // VEN-45: precise custom deadline (ISO). When present, the backend stores
  // it verbatim instead of rounding through whole hours.
  expires_at_iso?: string | null;
  strava_bet_type: string | null;
  strava_target_value: number | null;
  bet_mode?: string;
  pile_on_type?: string;
  target_telegram_id?: number;
  telegram_group_id?: number;
  // Creator TG id propagated from App.tsx owned-deep-link parser (VEN-10).
  // When set, the FundPage mount guard refuses to render for anyone whose
  // authenticated telegram_id does not match — defense-in-depth on top of
  // the App.tsx gate.
  creator_telegram_id?: number;
  // Set by CreatePage so we know to show the back button and restore the draft.
  _fromCreate?: boolean;
  _rawDraft?: unknown;
}

// VEN-42: match the real remaining time. The previous logic bucketed into
// 24h / 3 days / 1 week / 2 weeks / 1 month only, so a 2h custom deadline
// still rendered as "24h" and misled the creator on the Fund & Publish card.
function formatDeadline(hours: number, iso?: string | null): string {
  if (iso) {
    const diffMs = new Date(iso).getTime() - Date.now();
    if (Number.isFinite(diffMs) && diffMs > 0) {
      const mins = Math.round(diffMs / 60000);
      if (mins < 60) return `${mins}m`;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
    }
  }
  if (hours < 24) return `${hours}h`;
  if (hours <= 24) return "24h";
  if (hours <= 72) return "3 days";
  if (hours <= 168) return "1 week";
  if (hours <= 336) return "2 weeks";
  return "1 month";
}

export default function FundPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [tonConnectUI] = useTonConnectUI();
  const tonWallet = useTonWallet();
  const currentUser = useUser();

  const state = location.state as FundState | null;
  const [tonSent, setTonSent] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tonPrice, setTonPrice] = useState<number | null>(null);
  // Phase drives the full-screen ProcessingOverlay so the user can't
  // tap through / hit back during wallet connect, tx signing, or the
  // post-tx verification retry loop.
  const [phase, setPhase] = useState<ProcessingPhase>("idle");
  const [verifyAttempt, setVerifyAttempt] = useState(0);

  // VEN-75: auto-dismiss the "Connecting wallet..." overlay the moment a
  // wallet is actually connected OR after a timeout. On mobile the
  // tonConnectUI.openModal() redirect completes before the wallet hook
  // updates, so we need both a reactive check and a safety timeout.
  // The UI now shows the FUND swipe track automatically when tonWallet
  // is truthy, so we just dismiss the overlay and clear errors.
  useEffect(() => {
    if (phase === "connecting" && tonWallet) {
      setPhase("idle");
      setError(null);
    }
  }, [phase, tonWallet]);

  // Safety timeout: if "connecting" phase is stuck for >15s, dismiss it.
  // The UI now renders "Connect Wallet" vs "FUND" based on tonWallet,
  // so we only need to dismiss the overlay — the right button appears
  // automatically.
  useEffect(() => {
    if (phase !== "connecting") return;
    const timer = setTimeout(() => {
      setPhase("idle");
      setError(null);
    }, 15_000);
    return () => clearTimeout(timer);
  }, [phase]);
  const [resolvedTargetUser, setResolvedTargetUser] = useState(state?.targetUser ?? null);
  const [ownerBlocked, setOwnerBlocked] = useState(false);
  const [escrowAddress, setEscrowAddress] = useState<string | null>(null);
  const [showRetryPayment, setShowRetryPayment] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const lastPayloadRef = useRef<Record<string, unknown> | null>(null);
  const [hbStatus, setHbStatus] = useState<{ connected: boolean } | null>(null);
  useEffect(() => {
    api.get("/healthbridge/status").then((res) => setHbStatus(res.data)).catch(() => setHbStatus({ connected: false }));
  }, []);

  // Pre-fetch escrow address on mount so we can display it for user verification
  // before Tonkeeper shows the confirmation (VEN-21 safety check).
  useEffect(() => {
    api.get("/bets/escrow-address")
      .then((res) => {
        const addr = res.data?.address || res.data;
        if (typeof addr === "string") setEscrowAddress(addr);
      })
      .catch(() => {});
  }, []);

  // VEN-30: manual re-verify path for users whose deposit didn't confirm
  // within the 6×8s retry window. Calls /bets/reverify-deposit with a
  // wider 24h window; if the tx shows up now, re-submits the same bet
  // payload to /bets. If not, shows a helpful "still not indexed" hint.
  const handleRetryPayment = async () => {
    if (retrying || !lastPayloadRef.current || !state) return;
    setRetrying(true);
    setError(null);
    setPhase("verifying");
    try {
      const amount = Number(lastPayloadRef.current.amount);
      const check = await api.post(
        "/bets/reverify-deposit",
        null,
        { params: { amount, max_age_hours: 24 } },
      );
      if (!check.data?.verified) {
        setError("Deposit still not indexed on TonCenter. Try again in a minute.");
        setRetrying(false);
        setPhase("idle");
        return;
      }
      setProcessing(true);
      const res = await api.post("/bets", { ...lastPayloadRef.current });
      setSuccess(true);
      setProcessing(false);
      setPhase("success");
      setShowRetryPayment(false);
      const createdBetId = res?.data?.bet_id;
      if (createdBetId) sessionStorage.setItem("last_funded_bet", createdBetId);
      setTimeout(() => { if (createdBetId) navigate(`/bet/${createdBetId}`); }, 1200);
    } catch (err: unknown) {
      const backendDetail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(backendDetail || "Retry failed — please try again in a minute");
      setPhase("idle");
    } finally {
      setRetrying(false);
    }
  };

  // VEN-32: orphan refund path. If the deposit landed in escrow but the
  // bet/participant row was never written, normal cancel/refund can't
  // recover it. User taps "Refund my deposit" → backend verifies the
  // on-chain tx from their wallet and sends it back.
  const handleOrphanRefund = async () => {
    if (retrying || !lastPayloadRef.current) return;
    if (!confirm("Send your deposit back to your wallet? This only works if the transaction reached escrow but the bet was never created.")) return;
    setRetrying(true);
    setError(null);
    try {
      const amount = Number(lastPayloadRef.current.amount);
      const res = await api.post(
        "/bets/refund-orphan-deposit",
        null,
        { params: { amount, max_age_hours: 168 } },
      );
      if (res.data?.refunded) {
        setError(null);
        setShowRetryPayment(false);
        setSuccess(true);
        setTimeout(() => navigate("/"), 2000);
      } else {
        setError("Refund could not be processed. Try again or contact support.");
      }
    } catch (err: unknown) {
      const backendDetail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(backendDetail || "Refund failed — please try again in a minute");
    } finally {
      setRetrying(false);
    }
  };

  // Defense-in-depth ownership check (VEN-10): when the fund state carries
  // a creator_telegram_id (set by the App.tsx owned-deep-link parser), make
  // sure the authenticated user matches. This catches edge cases where the
  // App.tsx gate was skipped (e.g. stale tab, React StrictMode double-mount
  // with racing auth) before we ever touch TonConnect.
  useEffect(() => {
    if (!state?.creator_telegram_id) return;
    if (!currentUser) return; // wait until auth context resolves
    if (currentUser.telegram_id && currentUser.telegram_id === state.creator_telegram_id) return;
    setOwnerBlocked(true);
  }, [state?.creator_telegram_id, currentUser]);

  useEffect(() => {
    if (!state?.targetUsername || state.target_telegram_id) return;
    if (state.bet_mode !== "pile_on") return;
    if (state.pile_on_type !== "dare" && state.pile_on_type !== "versus") return;

    api.get("/users/potential-opponents", { params: { search: state.targetUsername } })
      .then((res) => {
        const users = Array.isArray(res.data) ? res.data : [];
        const match = users.find(
          (user: any) => (user.username || "").toLowerCase() === state.targetUsername?.toLowerCase()
        );
        if (match) {
          setResolvedTargetUser({
            telegram_id: match.telegram_id,
            name: match.name,
            username: match.username,
          });
        }
      })
      .catch(() => {});
  }, [state]);

  useEffect(() => {
    const lastBet = sessionStorage.getItem("last_funded_bet");
    if (lastBet && lastBet !== "pending") {
      navigate(`/bet/${lastBet}`, { replace: true });
      sessionStorage.removeItem("last_funded_bet");
    }
  }, [navigate]);

  // Fetch TON price for fiat display
  useEffect(() => {
    api
      .get("/crypto/price/toncoin")
      .then((res) => {
        const price = res.data?.price || res.data?.toncoin?.usd;
        if (typeof price === "number") setTonPrice(price);
      })
      .catch(() => setTonPrice(1.34));
  }, []);

  // No state = invalid entry
  if (!state) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Header title="FUND" showBack showClose />
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 32,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>{"⚠️"}</div>
          <div style={{ fontSize: 16, color: colors.grayLight }}>
            No bet data. Go back and create a bet first.
          </div>
        </div>
      </div>
    );
  }

  // Ownership guard (VEN-10) — see the useEffect above. If we detected that
  // the fund state's creator_telegram_id does not match the authenticated
  // user, refuse to render the fund UI at all.
  if (ownerBlocked) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Header title="FUND" showClose onClose={() => navigate("/", { replace: true })} />
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 32,
            textAlign: "center",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 48 }}>{"\uD83D\uDD12"}</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: colors.white }}>
            This bet isn't yours
          </div>
          <div style={{ fontSize: 14, color: colors.grayLight, lineHeight: 1.5, maxWidth: 320 }}>
            Only the person who posted this challenge can fund it.
          </div>
          <button
            onClick={() => navigate("/", { replace: true })}
            style={{
              marginTop: 12, padding: "12px 28px", borderRadius: 12,
              border: "none", background: colors.primary, color: colors.bg,
              fontSize: 15, fontWeight: 700, cursor: "pointer",
            }}
          >
            Browse my bets
          </button>
        </div>
      </div>
    );
  }

  const amountTon = parseFloat(state.amount);
  const amountNano = Math.round(amountTon * 1_000_000_000);
  const fiatAmount = tonPrice ? (amountTon * tonPrice).toFixed(2) : (amountTon * 1.34).toFixed(2);
  const isFitness = !!state.strava_bet_type;
  const cleanedDescription = cleanDescription(state.description);
  const effectiveTargetUser = resolvedTargetUser ?? state.targetUser;
  const effectiveTargetTelegramId = state.target_telegram_id || effectiveTargetUser?.telegram_id;
  const effectiveExpiresInHours = state.expires_in_hours || 24;

  const handleSwipeComplete = async () => {
    if (tonSent || phase !== "idle") return;

    // VEN-75: wallet connection now happens via the explicit "Connect Wallet"
    // button (rendered when tonWallet is null). The swipe track only renders
    // when connected, so this is a race-condition guard only.
    if (!tonConnectUI.account) {
      setError("Wallet disconnected — please reconnect");
      return;
    }

    // For dare/versus we need SOMEONE to address — either a known telegram_id
    // or a @username to invite via Telegram share sheet. Only block if we
    // have neither.
    if (
      state.bet_mode === "pile_on"
      && (state.pile_on_type === "dare" || state.pile_on_type === "versus")
      && !effectiveTargetTelegramId
      && !state.targetUsername
    ) {
      setError("Pick an opponent before funding.");
      return;
    }

    setTonSent(true);
    setProcessing(true);
    setError(null);

    try {
      // 1. Get escrow address
      const escrowRes = await api.get("/bets/escrow-address");
      const escrowFriendly = escrowRes.data?.address || escrowRes.data;
      // VEN-64: revert VEN-21's raw-address experiment. Raw `0:<hex>` works
      // in the TonConnect spec but some Tonkeeper builds simply don't open
      // the wallet sheet when handed that form, so users couldn't pay at
      // all. User-friendly (UQ…) reliably opens every wallet. Tonkeeper's
      // cosmetic re-encoding of the recipient string (VEN-21 original
      // complaint) is a smaller issue than blocking payments entirely; the
      // sanity-check "🔒 Escrow: UQC9…qNXY" line below the Fund button
      // remains so users can still verify the destination.
      const escrowForTx = escrowFriendly;

      // 2. Send TON via wallet
      const tx = {
        validUntil: Math.floor(Date.now() / 1000) + 300,
        messages: [
          {
            address: escrowForTx,
            amount: amountNano.toString(),
          },
        ],
      };

      // Save wallet address to backend
      const wallet = tonConnectUI.account;
      if (wallet?.address) {
        try {
          await api.put("/users/me/wallet", { ton_address: wallet.address, ton_address_raw: wallet.address });
        } catch (e) {
          // ignore if already saved
        }
      }

      // VEN-68: when Tonkeeper auto-closes after the user approves (instead of
      // them tapping Done), the TonConnect bridge sometimes never delivers a
      // response. Two things go wrong in the default setup:
      //   1. The SDK's "Confirm the transaction in Wallet" modal (the `before`
      //      modal, enabled by default) stays up forever, covering our UI.
      //   2. The sendTransaction promise never settles.
      //
      // Fix both: pass `modals: []` so the SDK never renders its own modal
      // (our SwipeTrack "⏳ Confirming…" is the only indicator), then race the
      // promise against a 60s timeout. On timeout, fall through to the POST
      // /bets retry loop — TonCenter verifies the deposit on-chain. Genuine
      // failures (rejection, bad amount) still reject sendTransaction before
      // the timer fires and are handled by the catch below.
      setPhase("signing");
      await Promise.race([
        tonConnectUI.sendTransaction(tx, { modals: [] }),
        new Promise((resolve) => setTimeout(resolve, 60_000)),
      ]);

      // 3. Create bet on backend (retry — TonCenter indexing takes ~30s)
      setPhase("verifying");
      const betPayload = {
        description: state.description,
        amount: amountNano,
        category: state.category || "custom",
        ...(effectiveTargetTelegramId ? { target_telegram_id: effectiveTargetTelegramId } : {}),
        ...(state.targetUsername ? { target_username: state.targetUsername } : {}),
        ...(state.telegram_group_id ? { telegram_group_id: state.telegram_group_id } : {}),
        expires_in_hours: effectiveExpiresInHours,
        ...(state.expires_at_iso ? { expires_at_iso: state.expires_at_iso } : {}),
        bet_type: "standard",
        referee_type: "creator",
        strava_bet_type: state.strava_bet_type,
        strava_target_value: state.strava_target_value,
        bet_mode: state.bet_mode || "1v1",
        pile_on_type: state.pile_on_type || null,
      };
      // Stash payload so the VEN-30 "I already paid — retry" button can
      // re-submit after the user manually kicks verification.
      lastPayloadRef.current = betPayload;

      let betRes = null;
      for (let attempt = 0; attempt < FUND_VERIFY_ATTEMPTS; attempt++) {
        setVerifyAttempt(attempt + 1);
        try {
          betRes = await api.post("/bets", { ...betPayload });
          break;
        } catch (e: any) {
          if (
            e.response?.status === 400
            && e.response?.data?.detail?.includes("not confirmed")
            && attempt < FUND_VERIFY_ATTEMPTS - 1
          ) {
            setError(null);
            setProcessing(true);
            await new Promise(r => setTimeout(r, 8000));
            continue;
          }
          throw e;
        }
      }

      setSuccess(true);
      setProcessing(false);
      setPhase("success");

      const createdBetId = betRes?.data?.bet_id;
      if (createdBetId) sessionStorage.setItem("last_funded_bet", createdBetId);

      if (!effectiveTargetTelegramId && createdBetId) {
        // Share deep link to accept page — don't use switchInlineQuery (it creates duplicate bet)
        const tg = (window as any).Telegram?.WebApp;
        const shareUrl = `https://t.me/sidebets_dev_bot/app?startapp=bet_${createdBetId.replace(/-/g, "")}`;
        // If this is a targeted invite (user picked @username that isn't on
        // SideBets yet) pre-fill the message with a mention so Telegram's
        // share sheet suggests that contact first.
        const mention = state.targetUsername ? `@${state.targetUsername} ` : "";
        const shareText = state.targetUsername
          ? `⚡ ${mention}I dare you: ${state.description} — ${state.amount} TON. Accept?`
          : `💪 I bet ${state.amount} TON that I'll ${state.description}. Match me?`;
        try {
          if (tg?.openTelegramLink) {
            tg.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`);
          } else {
            window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`);
          }
        } catch {
          // fallback: just navigate to bet detail
        }
        setTimeout(() => navigate(`/bet/${createdBetId}`), 2000);
      } else {
        setTimeout(() => navigate(`/bet/${createdBetId}`), 1500);
      }
    } catch (err) {
      setProcessing(false);
      setPhase("idle");
      setVerifyAttempt(0);

      const msg = err instanceof Error ? err.message : String(err);
      const backendDetail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;

      // Wallet not connected (race: disconnected between pre-flight and send)
      if (msg.includes("TON_CONNECT_SDK_ERROR") || /connect.*wallet/i.test(msg)) {
        setTonSent(false);
        setError("Connect your TON wallet to fund the bet");
      } else if (msg.includes("cancel") || msg.includes("reject") || msg.includes("closed")) {
        setTonSent(false);
        setError("Wallet cancelled — swipe to retry");
      } else if (backendDetail?.includes("not confirmed")) {
        // Retry loop exhausted — deposit taking too long to index on TonCenter.
        // VEN-30: show "I already paid — retry" button so user can kick
        // verification manually once indexing catches up, instead of
        // leaving them stuck with funds locked and no recovery path.
        setError("Deposit is taking longer than usual to confirm. Your funds are safe — you can retry verification once the wallet shows the tx as sent.");
        setShowRetryPayment(true);
      } else if (backendDetail) {
        // Show the backend's human-readable reason (wallet missing, bad deadline, etc.)
        setError(backendDetail);
      } else {
        setError(msg || "Something went wrong");
      }
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <ProcessingOverlay
        phase={phase}
        attempt={verifyAttempt}
        totalAttempts={FUND_VERIFY_ATTEMPTS}
      />
      <Header
        title="FUND & PUBLISH"
        showBack={!!state._fromCreate}
        showClose
        onBack={() => navigate("/create", { state: { draft: state._rawDraft } })}
        onClose={() => navigate("/")}
      />

      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: "0 16px 24px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Bet summary card */}
        <div
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: 16,
            marginBottom: 24,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{cleanedDescription}</span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: isFitness ? colors.success : colors.grayLight,
                textTransform: "uppercase",
              }}
            >
              {isFitness ? "AUTO-VERIFY \u2713" : "BOTH AGREE"}
            </span>
          </div>
          <div style={{ display: "flex", gap: 12, fontSize: 13, color: colors.grayLight }}>
            <span>{"\u23F0"} {formatDeadline(effectiveExpiresInHours, state.expires_at_iso)}</span>
            <span>vs {effectiveTargetUser?.username ? "@" + effectiveTargetUser.username : effectiveTargetUser?.name || (state.targetUsername ? "@" + state.targetUsername : "anyone")}</span>
            {state.pile_on_type && <span style={{ textTransform: "uppercase" }}>{state.pile_on_type}</span>}
          </div>
        </div>

        {/* HealthBridge connection warning — auto-verify needs HealthBridge connected */}
        {isFitness && hbStatus && !hbStatus.connected && (
          <div
            style={{
              background: "rgba(252,163,17,0.1)",
              border: "1px solid rgba(252,163,17,0.3)",
              borderRadius: 12,
              padding: "12px 16px",
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span style={{ fontSize: 20, flexShrink: 0 }}>{"\u26A0\uFE0F"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#FCA311", marginBottom: 4 }}>
                Connect HealthBridge for auto-verification
              </div>
              <div style={{ fontSize: 12, color: colors.grayLight, lineHeight: 1.4 }}>
                Without HealthBridge, results will need manual confirmation from both sides.
              </div>
            </div>
            <button
              onClick={() => navigate("/profile")}
              style={{
                background: colors.purple,
                border: "none",
                borderRadius: 8,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 700,
                color: colors.white,
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              Connect
            </button>
          </div>
        )}

        {/* Hero amount */}
        <div style={{ textAlign: "center", marginBottom: 8 }}>
          <div
            style={{
              fontSize: 48,
              fontWeight: 800,
              color: colors.primary,
              lineHeight: 1.1,
            }}
          >
            {state.amount} TON
          </div>
          <div style={{ fontSize: 15, color: colors.grayLight, marginTop: 4 }}>
            {"\u2248"} ${fiatAmount}
          </div>
        </div>

        {/* Info lines */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            marginTop: 20,
            marginBottom: 32,
          }}
        >
          <div
            style={{
              fontSize: 13,
              color: colors.grayLight,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {"\uD83D\uDD12"} Funds locked in escrow until resolution
          </div>
          <div style={{ fontSize: 13, color: colors.grayLight }}>
            {effectiveTargetUser || state.targetUsername
              ? `${effectiveTargetUser?.username ? "@" + effectiveTargetUser.username : effectiveTargetUser?.name || "@" + state.targetUsername} will be challenged to match your bet`
              : "Share in any chat after funding — first to accept plays"}
          </div>
        </div>

        {/* Spacer to push swipe to bottom */}
        <div style={{ flex: 1 }} />

        {/* VEN-75: when wallet is NOT connected, show a prominent Connect
            Wallet button instead of the swipe track. Once connected, switch
            to the FUND swipe. This avoids the "Opening wallet" stuck state
            that occurred when the swipe triggered openModal() and the user
            came back from Tonkeeper without the phase resetting. */}
        {!tonWallet ? (
          <>
            <button
              type="button"
              onClick={async () => {
                setError(null);
                setPhase("connecting");
                try {
                  await tonConnectUI.openModal();
                } catch {}
                // Phase is auto-dismissed by the useEffect watching tonWallet
                // or the 15s safety timeout.
              }}
              disabled={phase === "connecting"}
              style={{
                width: "100%",
                height: 56,
                borderRadius: 14,
                border: "none",
                background: colors.purple,
                color: colors.white,
                fontSize: 15,
                fontWeight: 700,
                cursor: phase === "connecting" ? "default" : "pointer",
                opacity: phase === "connecting" ? 0.7 : 1,
              }}
            >
              {phase === "connecting" ? "Connecting..." : "Connect Wallet"}
            </button>
            {error && (
              <div style={{ textAlign: "center", fontSize: 13, color: colors.danger, marginTop: 8 }}>
                {error}
              </div>
            )}
            <div style={{ textAlign: "center", fontSize: 12, color: colors.gray, marginTop: 10 }}>
              Connect your TON wallet to fund the bet
            </div>
          </>
        ) : (
          <>
            {/* Swipe track — wallet is connected */}
            <SwipeTrack
              label={`FUND \u2014 ${state.amount} TON`}
              onComplete={handleSwipeComplete}
              processing={processing}
              success={success}
              error={error}
            />

            {/* VEN-30: manual re-verify button after a "not confirmed"
                failure. Lets the user kick /reverify-deposit with a wider
                window once their wallet shows the tx as sent. */}
            {showRetryPayment && (
              <>
                <button
                  type="button"
                  onClick={handleRetryPayment}
                  disabled={retrying || processing}
                  style={{
                    width: "100%",
                    marginTop: 12,
                    padding: "12px 0",
                    borderRadius: 12,
                    border: `1px solid ${colors.border}`,
                    background: "transparent",
                    color: colors.white,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: retrying ? "wait" : "pointer",
                    opacity: retrying || processing ? 0.6 : 1,
                  }}
                >
                  {retrying ? "Checking..." : "I already paid \u2014 retry"}
                </button>
                {/* VEN-32: orphan refund escape hatch when retries don't help. */}
                <button
                  type="button"
                  onClick={handleOrphanRefund}
                  disabled={retrying || processing}
                  style={{
                    width: "100%",
                    marginTop: 8,
                    padding: "10px 0",
                    borderRadius: 12,
                    border: `1px solid ${colors.danger}`,
                    background: "transparent",
                    color: colors.danger,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: retrying ? "wait" : "pointer",
                    opacity: retrying || processing ? 0.6 : 1,
                  }}
                >
                  Refund my deposit back to wallet
                </button>
              </>
            )}

            <div
              style={{
                textAlign: "center",
                fontSize: 12,
                color: colors.gray,
                marginTop: 10,
              }}
            >
              Opens your wallet
            </div>
          </>
        )}

        {/* Escrow address sanity-check (VEN-21) — let user verify the
            address shown in Tonkeeper matches what we're actually sending to. */}
        {escrowAddress && (
          <div
            style={{
              textAlign: "center",
              fontSize: 11,
              color: colors.gray,
              marginTop: 8,
              paddingBottom: 8,
              lineHeight: 1.4,
              wordBreak: "break-all",
            }}
          >
            {"\uD83D\uDD12"} Escrow:{" "}
            <span style={{ fontFamily: "monospace", color: colors.grayLight }}>
              {escrowAddress.slice(0, 6)}...{escrowAddress.slice(-4)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
